import { createIntl, createIntlCache } from "react-intl";

import {
  isValidOnboardingWebsiteUrl,
  ONBOARDING_BUSINESS_NAME_MAX_LENGTH,
  ONBOARDING_BUSINESS_NAME_MIN_LENGTH,
  ONBOARDING_BUSINESS_WEBSITE_MAX_LENGTH,
} from "@/components/onboarding/OnboardingFormTypes";
import { messages } from "@/routes/adManager/localization/OnboardingForm.messages";

import type { IntlShape, MessageDescriptor } from "react-intl";
import type { OnboardingFormState } from "@/components/onboarding/OnboardingFormTypes";

const DEFAULT_INTL = createIntl({ locale: "en" }, createIntlCache());

function formatValidationMessage(
  intl: IntlShape | undefined,
  descriptor: MessageDescriptor,
  values?: Record<string, number>,
): string {
  return (intl ?? DEFAULT_INTL).formatMessage(descriptor, values);
}

function hasTrimmedValue(value: string): boolean {
  return value.trim().length > 0;
}

export function areOnboardingBusinessDetailsRequiredFieldsFilled(
  form: OnboardingFormState,
): boolean {
  return (
    hasTrimmedValue(form.businessName) &&
    hasTrimmedValue(form.businessWebsite) &&
    hasTrimmedValue(form.industryName)
  );
}

export function areOnboardingBusinessStepRequiredFieldsFilled({
  form,
  requiresTenantSelection,
  selectedTenantId,
}: {
  form: OnboardingFormState;
  requiresTenantSelection: boolean;
  selectedTenantId: string;
}): boolean {
  return (
    areOnboardingBusinessDetailsRequiredFieldsFilled(form) &&
    (!requiresTenantSelection || hasTrimmedValue(selectedTenantId))
  );
}

export function areOnboardingAccountDetailsRequiredFieldsFilled(
  form: OnboardingFormState,
): boolean {
  return (
    hasTrimmedValue(form.countryCode) &&
    hasTrimmedValue(form.currencyCode) &&
    hasTrimmedValue(form.timezone) &&
    hasTrimmedValue(form.advertiserType) &&
    (form.advertiserType !== "business" || hasTrimmedValue(form.agencyAnswer))
  );
}

export function areOnboardingCreateAccountRequiredFieldsFilled(form: OnboardingFormState): boolean {
  return (
    areOnboardingBusinessDetailsRequiredFieldsFilled(form) &&
    areOnboardingAccountDetailsRequiredFieldsFilled(form)
  );
}

export function getOnboardingBusinessNameError(
  businessName: string,
  intl?: IntlShape,
): string | null {
  const trimmedBusinessName = businessName.trim();
  if (!trimmedBusinessName) {
    return formatValidationMessage(intl, messages.enterBusinessNameError);
  }
  if (
    trimmedBusinessName.length < ONBOARDING_BUSINESS_NAME_MIN_LENGTH ||
    trimmedBusinessName.length > ONBOARDING_BUSINESS_NAME_MAX_LENGTH
  ) {
    return formatValidationMessage(intl, messages.businessNameLengthError, {
      minimumLength: ONBOARDING_BUSINESS_NAME_MIN_LENGTH,
      maximumLength: ONBOARDING_BUSINESS_NAME_MAX_LENGTH,
    });
  }
  return null;
}

export function getOnboardingWebsiteError(website: string, intl?: IntlShape): string | null {
  const trimmedWebsite = website.trim();
  if (!trimmedWebsite) {
    return formatValidationMessage(intl, messages.enterBusinessWebsiteError);
  }
  if (trimmedWebsite.length > ONBOARDING_BUSINESS_WEBSITE_MAX_LENGTH) {
    return formatValidationMessage(intl, messages.businessWebsiteLengthError, {
      maximumLength: ONBOARDING_BUSINESS_WEBSITE_MAX_LENGTH,
    });
  }
  if (!isValidOnboardingWebsiteUrl(trimmedWebsite)) {
    return formatValidationMessage(intl, messages.businessWebsiteFormatError);
  }
  return null;
}

export function getBusinessStepError(
  {
    form,
    effectiveTenantId,
    shouldCreateTenant,
    tenantsLoading,
    tenantsError,
    allowMissingTenant = false,
  }: {
    form: OnboardingFormState;
    effectiveTenantId: string;
    shouldCreateTenant: boolean;
    tenantsLoading: boolean;
    tenantsError: boolean;
    allowMissingTenant?: boolean;
  },
  intl?: IntlShape,
): string | null {
  if (tenantsLoading) {
    return formatValidationMessage(intl, messages.loadingTenantsError);
  }
  if (tenantsError) {
    return formatValidationMessage(intl, messages.failedToLoadTenants);
  }
  if (!shouldCreateTenant && !effectiveTenantId && !allowMissingTenant) {
    return formatValidationMessage(intl, messages.selectTenantError);
  }
  const businessNameError = getOnboardingBusinessNameError(form.businessName, intl);
  if (businessNameError) {
    return businessNameError;
  }
  const websiteError = getOnboardingWebsiteError(form.businessWebsite, intl);
  if (websiteError) {
    return websiteError;
  }
  if (!form.industryName.trim()) {
    return formatValidationMessage(intl, messages.enterIndustryError);
  }
  return null;
}
